import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DataItem } from '@app/components/shared/model/data-item';
import { FenseResultButtonComponent } from '@app/components/fense-result/fense-result-button/fense-result-button.component';
import { ChipListComponent } from '@app/components/shared/chip-list/chip-list.component';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { FenceResultFacadeService } from '@app/facade-services/fence-result/fence-result-facade.service';
import { ResultCodeFacadeService } from '@app/facade-services/result-code/result-code-facade.service';
import { SelectedRider } from '@app/components/fense-result/model/selectedRider';
import { SessionService } from '@app/services/session/session.service';
import { SessionKeys } from '@app/constants/session-keys';
import { RiderView } from '@app/facade-services/rider-view/model/rider-view';
import {
  mapFence,
  mapRiderView,
} from '@app/domain/data-item-mapper/data-item-mapper';
import { MatCard } from '@angular/material/card';
import { SelectOptionChipComponent } from '@app/components/shared/select-option-chip/select-option-chip.component';
import {
  defined,
  distict,
  formatDateUTC,
  isEmpty,
  isNotEmpty,
  withRetryAndTimeout,
} from '@app/fremework/functions';
import { hasAllResult, raceViewFences } from '@app/domain/functions';

import { PageLayoutComponent } from '@app/components/shared/page-layout/page-layout.component';
import { PageTitleComponent } from '@app/components/common/page-title/page-title.component';
import { RaceViewService } from '@app/facade-services/race-view/race-view.service';
import { SliceState } from '@app/store/faxe/sapiens-entity-state';
import { RepportFenceEvent } from '@app/components/fense-result/fense-result-button/repport-fence-event';
import { RepportFenceRider } from '@app/components/fense-result/model/repported-fence-rider';
import {
  Fence,
  FenceResultPost,
  ResultCode,
  Rider,
} from '@app/rest-client/faxe/models';
import { PasswordService } from '@app/services/password/password.service';
import { UserToken } from '@app/services/password/model/user-token';
import { RollingHistListComponent } from '@app/components/shared/rolling-hist-list/rolling-hist-list.component';
import { ListItem } from '@app/components/shared/rolling-hist-list/model/list-item';
import { RollingHistListService } from '@app/components/shared/rolling-hist-list/service/rolling-hist-list.service';
import { ClubbLoggoService } from '@app/services/clubb-loggo.service';
import { FormsModule } from '@angular/forms';
import { FenceResultsService } from '@app/rest-client/faxe/services/fence-results.service';

@Component({
  selector: 'faxe-fense-result',
  templateUrl: './fense-result.component.html',
  styleUrls: ['./fense-result.component.scss'],
  imports: [
    MatCard,
    SelectOptionChipComponent,
    ChipListComponent,
    FenseResultButtonComponent,
    PageLayoutComponent,
    PageTitleComponent,
    RollingHistListComponent,
    FormsModule,
  ],
})
export class FenseResultComponent implements OnInit, OnDestroy {
  @ViewChild('fenseResultButton') fenseResultButton: FenseResultButtonComponent;
  @ViewChild('riderComponent') riderComponent: ChipListComponent<
    DataItem<Rider>
  >;
  fenceDataItems: DataItem<Fence>[];
  riderViewDataItems: DataItem<RiderView>[];
  riderViews: RiderView[];
  selectedFensesDataItem: DataItem<Fence>[];
  selectedFenses: Fence[] = [];
  selectedRider: SelectedRider;
  previouslySelectedFenses: string[] = [];
  preSelectedResultCodes: string[];
  reportedFencesRiderList: RepportFenceRider[] = [];
  protected readonly length = length;
  protected readonly defined = defined;
  private unsubscribe$ = new Subject<void>();
  userToken: UserToken;
  histListBucketId = 'fence-result';
  listItems: ListItem<any>[] = [];

  constructor(
    private readonly fenceResultFacadeService: FenceResultFacadeService,
    private readonly resultCodeFacadeService: ResultCodeFacadeService,
    private readonly raceViewService: RaceViewService,
    private readonly sessionService: SessionService,
    private readonly passwordService: PasswordService,
    private readonly rollingHistListService: RollingHistListService,
    private readonly clubbLoggoService: ClubbLoggoService,
    private readonly fenceResultsService: FenceResultsService
  ) {}

  ngOnInit(): void {
    this.passwordService
      .raceUserToken()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((token) => {
        this.userToken = token;
      });

    this.fetchAllResultCodes();
    this.fetchRaceView();
    this.preselectedFences();

    this.rollingHistListService.histListMap$
      .pipe(
        takeUntil(this.unsubscribe$),
        filter((h) => h?.has(this.histListBucketId))
      )
      .subscribe((histListMap) => {
        this.listItems = histListMap.get(this.histListBucketId);
      });
  }

  preselectedFences(): void {
    this.fenceResultFacadeService
      .selectFenceResultByRaceGuid(
        this.raceViewService.selectedRaceGuid(),
        SliceState.reportable
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((fenceResultPosts) => {
        this.preSelectedResultCodes = fenceResultPosts
          ?.filter(
            (fenceResultPost) =>
              defined(this.selectedRider) &&
              fenceResultPost.horseCombinationNumber ===
                this.selectedRider?.horseCombinationNumber
          )
          .map((fenceResult) => fenceResult.resultCodeGuid);
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$?.next();
    this.unsubscribe$?.complete();
  }

  setFences(fences: Fence[]): void {
    this.fenceDataItems = distict(fences, 'fenceName')
      .sort((n1, n2) => n1.renumberedSequence - n2.renumberedSequence)
      .map((fence) => mapFence(fence));

    if (this.hasPreviousSelectedFences()) {
      this.previouslySelectedFenses = this.getPreviousSelectedFences();
    }
  }

  fetchAllResultCodes(): void {
    this.resultCodeFacadeService
      .fetchAllResultCodes('FenseResultComponent.fetchAllResultCodes')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe();
  }

  fetchRaceView(): void {
    this.raceViewService
      .selectRaceView(
        false,
        true,
        false,
        'FenseResultComponent.fetchRiderViews'
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((raceView) => {
        if (isEmpty(this.fenceDataItems)) {
          this.setFences(raceViewFences(raceView));
        }

        this.riderViews = raceView.riders
          .filter((riderView) => riderView.isReportable)
          .sort(
            (r1, r2) => r1.horseCombinationNumber - r2.horseCombinationNumber
          );

        this.riderViewDataItems = this.riderViews.map((riderView) =>
          mapRiderView(riderView)
        );
        if (this.selectedFenses) {
          this.filterOnResult();
        }
      });
  }

  fenceChange(selectedFences: Fence[]) {
    this.sessionService.set(
      SessionKeys.selectedFences,
      selectedFences.map((sf) => sf.fenceGuid).join(',')
    );
    this.selectedFenses = selectedFences;
    this.selectedFensesDataItem = selectedFences?.map((fence) =>
      mapFence(fence)
    );
    this.filterOnResult();
  }

  hasPreviousSelectedFences(): boolean {
    return this.sessionService.hasKey(SessionKeys.selectedFences);
  }

  getPreviousSelectedFences(): string[] {
    return this.sessionService.get(SessionKeys.selectedFences).split(',');
  }

  filterOnResult() {
    if (isEmpty(this.selectedFenses)) {
      return;
    }

    this.riderViewDataItems = this.riderViews
      .map((riderView) => mapRiderView(riderView))
      ?.filter((item) => !hasAllResult(item.value, this.selectedFenses))
      ?.sort((a, b) => b.sortOrder - a.sortOrder);
    if (isNotEmpty(this.riderViewDataItems)) {
      this.riderViewDataItems[0].glow = true;
    }
  }

  onRiderSelected($event: DataItem<RiderView>) {
    this.selectedRider = this.mapSelectedRider($event, +$event.displayname);
    this.preSelectedResultCodes = undefined;

    this.preSelectedResultCodes = this.reportedFencesRiderList
      .filter((reported) => reported.riderGuid === this.selectedRider.riderGuid)
      .flatMap((reportFenceRider) => reportFenceRider.repportFenceEvent)
      .filter((reportedFenceEvent) =>
        this.selectedFenses.find(
          (fence) => reportedFenceEvent.fenceName === fence.fenceName
        )
      )
      .flatMap((reportedFenceEvent) =>
        reportedFenceEvent.resultCodes.map((r) => r.resultCodeGuid)
      );
  }

  mapSelectedRider(
    dataItem: DataItem<RiderView>,
    defaultNumber: number
  ): SelectedRider {
    return {
      riderGuid: dataItem?.value?.riderGuid,
      horseCombinationNumber: defaultNumber,
      riderName: dataItem?.value?.riderName,
      clubName: dataItem?.value?.clubName,
    };
  }

  onRiderLastUnSelected() {
    this.clear();
  }

  clear() {
    this.selectedRider = undefined;
    this.riderComponent?.clear();
    this.fenseResultButton?.clear();
  }

  submit(fenceName: string, resultCodes: ResultCode[]) {
    const fenceResultRequests: FenceResultPost[] = resultCodes?.map(
      (resultCode) => ({
        fenceName,
        resultCodeGuid: resultCode.resultCodeGuid,
        horseCombinationNumber: this.selectedRider.horseCombinationNumber,
        riderGuid: this.selectedRider.riderGuid,
      })
    );

    this.rollingHistListService.unshift(this.histListBucketId, {
      key: this.selectedRider.horseCombinationNumber,
      heading: `Ryttarnummer ${this.selectedRider.horseCombinationNumber}`,
      h2: `Rapporterad på hinder ${fenceName}: ${resultCodes
        .map((r) => r.shortName)
        .join(', ')}`,
      h3: this.selectedRider.riderName,
      logoSrc: this.clubbLoggoService.logoSrc(this.selectedRider.clubName),
    });

    withRetryAndTimeout(
      this.fenceResultsService.fenceResultPost.bind(this.fenceResultsService),
      [
        {
          raceGuid: this.userToken.selectedRaceGuid,
          timestamp: formatDateUTC(new Date()),
          body: fenceResultRequests,
        },
      ]
    ).subscribe({
      next: () => {
        this.rollingHistListService.setVerified(
          this.histListBucketId,
          this.selectedRider.horseCombinationNumber
        );
        if (this.isAllReported()) {
          this.removeRider(this.selectedRider);
          this.clear();
          this.reportedFencesRiderList = this.reportedFencesRiderList.filter(
            (r) => r.riderGuid !== this.selectedRider.riderGuid
          );
        }
      },
      error: (err) => {
        console.log('Failed to report fence result:', err);
        this.clear();
      },
    });
  }

  removeRider(selectedRider: SelectedRider) {
    this.riderViewDataItems = this.riderViewDataItems?.filter(
      (riderDataItem) =>
        riderDataItem.value?.horseCombinationNumber !==
        selectedRider.horseCombinationNumber
    );
  }

  addReportedFencesRiderList(repportFenceEvent: RepportFenceEvent): void {
    const reportedFenceRider = this.reportedFencesRiderList.find(
      (reportedFenceRider) =>
        reportedFenceRider.riderGuid === this.selectedRider.riderGuid
    );

    if (defined(reportedFenceRider)) {
      reportedFenceRider.repportFenceEvent.push(repportFenceEvent);
    } else {
      this.reportedFencesRiderList.push({
        riderGuid: this.selectedRider.riderGuid,
        repportFenceEvent: [repportFenceEvent],
      });
    }
  }

  isAllReported() {
    return this?.selectedFensesDataItem.every((fenceDataItem) =>
      this.reportedFencesRiderList.find(
        (reportFenceRider) =>
          defined(
            reportFenceRider.repportFenceEvent.find(
              (r) => r.fenceName === fenceDataItem.value.fenceName
            )
          ) && reportFenceRider.riderGuid === this.selectedRider.riderGuid
      )
    );
  }

  fenceRepported($event: RepportFenceEvent) {
    this.addReportedFencesRiderList($event);
    this.submit($event.fenceName, $event.resultCodes);
  }

  protected readonly isNotEmpty = isNotEmpty;
}
